/**
 * Copyright (c) Streamlit Inc. (2018-2022) Snowflake Inc. (2022)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from "react"
import { isMobile } from "react-device-detect"
import { ChevronDown } from "baseui/icon"
import { Select as UISelect, OnChangeParams, Option } from "baseui/select"
import { logWarning } from "src/lib/util/log"
import VirtualDropdown from "src/lib/components/shared/Dropdown/VirtualDropdown"
import { hasMatch, score } from "fzy.js"
import _ from "lodash"
import { LabelVisibilityOptions } from "src/lib/util/utils"
import { Placement } from "src/lib/components/shared/Tooltip"
import TooltipIcon from "src/lib/components/shared/TooltipIcon"
import {
  WidgetLabel,
  StyledWidgetLabelHelp,
} from "src/lib/components/widgets/BaseWidget"
import { iconSizes } from "src/lib/theme/primitives"

export interface Props {
  disabled: boolean
  width?: number
  value: number
  onChange: (value: number) => void
  options: any[]
  label?: string | null
  labelVisibility?: LabelVisibilityOptions
  help?: string
}

interface State {
  // Used to work around the forced rerender when the input is empty
  isEmpty: boolean
  /**
   * The value specified by the user via the UI. If the user didn't touch this
   * widget's UI, the default value is used.
   */
  value: number
}

interface SelectOption {
  label: string
  value: string
}

// Add a custom filterOptions method to filter options only based on labels.
// The baseweb default method filters based on labels or indices
// More details: https://github.com/streamlit/streamlit/issues/1010
// Also filters using fuzzy search powered by fzy.js. Automatically handles
// upper/lowercase.
export function fuzzyFilterSelectOptions(
  options: SelectOption[],
  pattern: string
): readonly SelectOption[] {
  if (!pattern) {
    return options
  }

  return _(options)
    .filter((opt: SelectOption) => hasMatch(pattern, opt.label))
    .sortBy((opt: SelectOption) => score(pattern, opt.label))
    .reverse()
    .value()
}

class Selectbox extends React.PureComponent<Props, State> {
  public state: State = {
    isEmpty: false,
    value: this.props.value,
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (
      prevProps.value !== this.props.value &&
      this.state.value !== this.props.value
    ) {
      this.setState((_, prevProps) => {
        return { value: prevProps.value }
      })
    }
  }

  private onChange = (params: OnChangeParams): void => {
    if (params.value.length === 0) {
      logWarning("No value selected!")
      return
    }

    const [selected] = params.value

    this.setState({ value: parseInt(selected.value, 10) }, () =>
      this.props.onChange(this.state.value)
    )
  }

  /**
   * Both onInputChange and onClose handle the situation where
   * the user has hit backspace enough times that there's nothing
   * left in the input, but we don't want the value for the input
   * to then be invalid once they've clicked away
   */
  private onInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const currentInput = event.target.value

    this.setState({
      isEmpty: !currentInput,
    })
  }

  private onClose = (): void => {
    this.setState({
      isEmpty: false,
    })
  }

  private filterOptions = (
    options: readonly Option[],
    filterValue: string
  ): readonly Option[] =>
    fuzzyFilterSelectOptions(options as SelectOption[], filterValue)

  public render(): React.ReactNode {
    const style = { width: this.props.width }
    const { label, labelVisibility, help } = this.props
    let { disabled, options } = this.props

    let value = [
      {
        label:
          options.length > 0
            ? options[this.state.value]
            : "No options to select.",
        value: this.state.value.toString(),
      },
    ]

    if (this.state.isEmpty) {
      value = []
    }

    if (options.length === 0) {
      options = ["No options to select."]
      disabled = true
    }

    const selectOptions: SelectOption[] = options.map(
      (option: string, index: number) => ({
        label: option,
        value: index.toString(),
      })
    )

    // Check if we have more than 10 options in the selectbox.
    // If that's true, we show the keyboard on mobile. If not, we hide it.
    const showKeyboardOnMobile = options.length > 10

    return (
      <div className="row-widget stSelectbox" style={style}>
        <WidgetLabel
          label={label}
          labelVisibility={labelVisibility}
          disabled={disabled}
        >
          {help && (
            <StyledWidgetLabelHelp>
              <TooltipIcon content={help} placement={Placement.TOP_RIGHT} />
            </StyledWidgetLabelHelp>
          )}
        </WidgetLabel>
        <UISelect
          clearable={false}
          disabled={disabled}
          labelKey="label"
          aria-label={label || ""}
          onChange={this.onChange}
          onInputChange={this.onInputChange}
          onClose={this.onClose}
          options={selectOptions}
          filterOptions={this.filterOptions}
          value={value}
          valueKey="value"
          overrides={{
            Root: {
              style: () => ({
                lineHeight: 1.4,
              }),
            },
            Dropdown: { component: VirtualDropdown },

            ControlContainer: {
              style: () => ({
                // Baseweb requires long-hand props, short-hand leads to weird bugs & warnings.
                borderLeftWidth: "1px",
                borderRightWidth: "1px",
                borderTopWidth: "1px",
                borderBottomWidth: "1px",
              }),
            },

            IconsContainer: {
              style: () => ({
                paddingRight: ".5rem",
              }),
            },

            ValueContainer: {
              style: () => ({
                // Baseweb requires long-hand props, short-hand leads to weird bugs & warnings.
                paddingRight: ".5rem",
                paddingLeft: ".5rem",
                paddingBottom: ".5rem",
                paddingTop: ".5rem",
              }),
            },

            Input: {
              props: {
                // Change the 'readonly' prop to hide the mobile keyboard if options < 10
                readOnly:
                  isMobile && showKeyboardOnMobile === false
                    ? "readonly"
                    : null,
              },
              style: () => ({
                lineHeight: 1.4,
              }),
            },

            // Nudge the dropdown menu by 1px so the focus state doesn't get cut off
            Popover: {
              props: {
                overrides: {
                  Body: {
                    style: () => ({
                      marginTop: "1px",
                    }),
                  },
                },
              },
            },

            SelectArrow: {
              component: ChevronDown,

              props: {
                overrides: {
                  Svg: {
                    style: () => ({
                      width: iconSizes.xl,
                      height: iconSizes.xl,
                    }),
                  },
                },
              },
            },
          }}
        />
      </div>
    )
  }
}

export default Selectbox
